import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { LayoutDashboard, Wrench, Flame, Footprints, FlaskConical, Cog, Coffee, Cross, ArrowUpFromLine, Siren, FileBarChart, ScanLine, Users, Menu, LogOut, WifiOff, RefreshCw, Eye, Building2, Network, Droplets, Route, MapPinned, ListChecks, ClipboardCheck, Warehouse, GraduationCap } from "lucide-react";
import { MANAGERS } from "../lib/api";
import { useSite, SiteSelector } from "../lib/site";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";
import { getQueue, syncQueue } from "../lib/offline";
import { Sheet, SheetContent, SheetTrigger } from "./ui/sheet";
import { Notifications } from "./Notifications";
import { ThemeToggle } from "../lib/theme";
import { BrandLogo } from "./BrandLogo";

const NAV = [
  ["/group", "Group Dashboard", Network], ["/", "Site Dashboard", LayoutDashboard], ["/my-checks", "My Machine Checks", ClipboardCheck, "puwer", "check"], ["/maintenance", "Maintenance", Wrench, "maintenance"], ["/fire", "Fire Safety", Flame, "fire"],
  ["/inspect/fire_walkaround", "Fire Walkaround", Footprints, "fire", "check"], ["/audits", "Pathway Audits", Route, "audit"], ["/coshh", "COSHH & Chemicals", FlaskConical, "coshh"], ["/puwer", "Machine Safety", Cog, "puwer"],
  ["/welfare", "Worker Welfare", Coffee, "welfare"], ["/first-aid", "First Aid", Cross, "first_aid"], ["/ladders", "Ladders", ArrowUpFromLine, "ladders"], ["/legionella", "Legionella", Droplets, "legionella"], ["/asrs", "ASRS & Cranes", Warehouse, "asrs"],
  ["/incidents", "Incidents & CAPA", Siren, "incidents"], ["/training", "Training & Toolbox Talks", GraduationCap, "training"], ["/reports", "Reports", FileBarChart], ["/scan", "Scan QR / Barcode", ScanLine],
];

const NavItems = ({ onClick, isAdmin, isOwner, isMgr }) => {
  const { can } = useAuth();
  const items = [...NAV.filter(([, , , m, l]) => !m || can(m, l || "view")), ...(isMgr ? [["/site-setup", "Site Setup", MapPinned], ["/question-library", "Question Library", ListChecks]] : []), ...(isAdmin ? [["/users", "Users & Roles", Users]] : []), ...(isOwner ? [["/sites", "Sites", Building2]] : [])];
  return (
  <nav className="space-y-0.5 px-2">
    {items.map(([to, label, Icon]) => (
      <NavLink key={to} to={to} end={to === "/"} onClick={onClick} data-testid={`nav-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
        className={({ isActive }) => `flex h-11 items-center gap-3 rounded-r-md border-l-4 px-3 text-sm transition-colors ${isActive ? "border-rose-500 bg-slate-800 font-semibold text-rose-300" : "border-transparent text-slate-300 hover:bg-slate-800/60"}`}>
        <Icon className="h-4 w-4 shrink-0" /> {label}
      </NavLink>
    ))}
  </nav>
  );
};

const Brand = () => (
  <div className="px-5 py-5" data-testid="sidebar-brand">
    <BrandLogo className="h-9" />
    <p className="font-display mt-2 whitespace-nowrap text-sm font-bold uppercase tracking-wide">Compliance Pro</p>
    <p className="font-mono text-[9px] tracking-[0.18em] text-slate-400">UK COMPLIANCE CONTROL</p>
  </div>
);

const OfflineBar = () => {
  const [online, setOnline] = useState(navigator.onLine);
  const [count, setCount] = useState(getQueue().length);
  useEffect(() => {
    const upd = () => { setOnline(navigator.onLine); setCount(getQueue().length); };
    const goOnline = async () => { upd(); const n = await syncQueue(); if (n) toast.success(`${n} offline inspection(s) synced`); };
    window.addEventListener("online", goOnline);
    window.addEventListener("offline", upd);
    window.addEventListener("eps-queue", upd);
    goOnline();
    return () => { window.removeEventListener("online", goOnline); window.removeEventListener("offline", upd); window.removeEventListener("eps-queue", upd); };
  }, []);
  if (online && !count) return null;
  return (
    <div className={`flex items-center justify-between gap-2 px-4 py-2 text-xs ${online ? "bg-cyan-900/60 text-cyan-100" : "bg-amber-900/70 text-amber-100"}`} data-testid="offline-banner">
      <span className="flex items-center gap-2"><WifiOff className="h-4 w-4" />{online ? "Back online" : "Offline mode - inspections are saved on this device"} - {count} queued</span>
      {online && count > 0 && (
        <button onClick={async () => toast.success(`${await syncQueue()} synced`)} className="flex items-center gap-1 underline" data-testid="sync-now-btn"><RefreshCw className="h-3 w-3" />Sync now</button>
      )}
    </div>
  );
};

export default function Layout() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const nav = useNavigate();
  const isAdmin = ["admin", "site_manager"].includes(user.role);
  const isOwner = user.role === "admin";
  const isMgr = MANAGERS.includes(user.role);
  const { site, loaded } = useSite();

  return (
    <div className="flex min-h-screen">
      <aside className="sticky top-0 hidden h-screen w-64 shrink-0 overflow-y-auto border-r border-slate-800 bg-ink lg:block">
        <Brand />
        <NavItems isAdmin={isAdmin} isOwner={isOwner} isMgr={isMgr} />
      </aside>
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-30 flex h-16 items-center gap-2 border-b border-slate-800 bg-ink/90 px-3 backdrop-blur-md sm:px-6">
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button className="flex h-11 w-11 items-center justify-center rounded-md hover:bg-slate-800 lg:hidden" data-testid="mobile-menu-btn"><Menu className="h-5 w-5" /></button>
            </SheetTrigger>
            <SheetContent side="left" className="w-72 overflow-y-auto border-slate-800 bg-ink p-0">
              <Brand />
              <NavItems isAdmin={isAdmin} isOwner={isOwner} isMgr={isMgr} onClick={() => setOpen(false)} />
            </SheetContent>
          </Sheet>
          <SiteSelector />
          <div className="flex-1" />
          <button onClick={() => nav("/scan")} className="hidden h-11 items-center gap-2 rounded-md border border-slate-700 px-3 text-sm hover:bg-slate-800 sm:flex" data-testid="header-scan-btn"><ScanLine className="h-4 w-4" />Scan</button>
          <ThemeToggle />
          {site && <Notifications key={site.id} />}
          <div className="hidden text-right sm:block">
            <p className="text-sm font-semibold leading-tight" data-testid="current-user-name">{user.name}</p>
            <p className="eyebrow text-[9px]" data-testid="current-user-role">{user.role_label}</p>
          </div>
          <button onClick={logout} className="flex h-11 w-11 items-center justify-center rounded-md hover:bg-slate-800" data-testid="logout-btn" title="Sign out"><LogOut className="h-5 w-5" /></button>
        </header>
        {user.role === "auditor" && (
          <div className="flex items-center gap-2 bg-cyan-950 px-4 py-2 text-xs text-cyan-200" data-testid="auditor-banner"><Eye className="h-4 w-4" />Auditor mode - read-only access</div>
        )}
        <OfflineBar />
        <main className="grid-bg flex-1 p-4 pb-24 sm:p-6 lg:p-8 lg:pb-8">
          {!loaded ? <p className="text-slate-500">Loading sites...</p>
            : site || isOwner ? <Outlet key={site?.id} />
            : <p className="panel p-8 text-center text-slate-400" data-testid="no-site-message">You have not been assigned to a site yet. Ask your administrator for access.</p>}
        </main>
        <nav className="no-print fixed inset-x-0 bottom-0 z-30 grid grid-cols-4 border-t border-slate-800 bg-ink/95 backdrop-blur-md lg:hidden">
          {[["/", "Home", LayoutDashboard], ["/scan", "Scan", ScanLine], ["/audits", "Audit", Route], ["/incidents?new=1", "Report", Siren]].map(([to, l, I]) => (
            <NavLink key={to} to={to} end className="flex h-16 flex-col items-center justify-center gap-1 text-[11px] text-slate-300" data-testid={`bottom-nav-${l.toLowerCase()}`}><I className="h-5 w-5" />{l}</NavLink>
          ))}
        </nav>
      </div>
    </div>
  );
}
